import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import { AttachedFile } from "../types/attachedfile";

interface Props {
    files: AttachedFile[];
    onRemove: (id: string) => void;
}

export function MediaPreviewStrip({ files, onRemove }: Props) {
    if (!files.length) return null;

    return (
        <Box
            sx={{
                px: 1.5,
                py: 1,
                borderTop: "1px solid",
                borderColor: "divider",
                backgroundColor: "grey.50",
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                {files.length} archivo{files.length > 1 ? "s" : ""} adjunto{files.length > 1 ? "s" : ""}
            </Typography>

            <Box
                display="flex"
                gap={1}
                flexWrap="wrap"
            >
                {files.map((f) => (
                    <Box
                        key={f.id}
                        sx={{
                            position: "relative",
                            borderRadius: 1.5,
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            transition: "all 0.15s ease",
                            "&:hover": {
                                boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                                borderColor: "action.disabled",
                            },
                            "&:hover .remove-btn": {
                                opacity: 1,
                            },
                        }}
                    >
                        {f.file.type.startsWith("image/") ? (
                            // Imagen preview
                            <Box
                                component="img"
                                src={f.previewUrl}
                                alt={f.file.name}
                                sx={{
                                    width: 80,
                                    height: 80,
                                    objectFit: "cover",
                                    display: "block",
                                }}
                            />
                        ) : (
                            // Audio / archivo preview
                            <Box
                                sx={{
                                    width: 120,
                                    height: 60,
                                    bgcolor: "grey.100",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    px: 1,
                                }}
                            >
                                <AudiotrackIcon
                                    sx={{
                                        fontSize: 20,
                                        color: "text.secondary",
                                        flexShrink: 0,
                                    }}
                                />
                                <Box
                                    sx={{
                                        fontSize: 12,
                                        color: "text.secondary",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                    title={f.file.name}
                                >
                                    {f.file.name}
                                </Box>
                            </Box>
                        )}

                        {/* Botón eliminar */}
                        <Tooltip title="Eliminar">
                            <IconButton
                                className="remove-btn"
                                size="small"
                                onClick={() => onRemove(f.id)}
                                sx={{
                                    position: "absolute",
                                    top: 4,
                                    right: 4,
                                    width: 24,
                                    height: 24,
                                    bgcolor: "rgba(0,0,0,0.6)",
                                    color: "#fff",
                                    opacity: 0,
                                    transition: "opacity 0.15s, background-color 0.15s",
                                    "&:hover": {
                                        bgcolor: "rgba(0,0,0,0.8)",
                                    },
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}